// Scene lighting. Adds an ambient light, a Sun directional light, a fill
// light, a hemisphere light, and a back light. All five draw from the
// optional `lighting` block of scene.json (color/intensity/position keys);
// missing values fall back to hard-coded defaults.

import type * as THREE from 'three';
import type { LightingConfig } from '../app/context';

interface LightingDeps {
    THREE: typeof import('three');
    scene: THREE.Scene;
    mapPhysicalToThreeWithRotation: (p: THREE.Vector3) => THREE.Vector3;
    lighting: LightingConfig | null;
}

export function setupLighting({ THREE, scene, mapPhysicalToThreeWithRotation, lighting }: LightingDeps): void {
    const ambientColor = (lighting && lighting.ambient && lighting.ambient.color) || '#404040';
    const ambientIntensity = (lighting && lighting.ambient && lighting.ambient.intensity) || 0.6;
    const ambientLight = new THREE.AmbientLight(new THREE.Color(ambientColor), ambientIntensity);
    scene.add(ambientLight);

    // Sun directional light at Sun GSM position if available
    let sunDirColor: string = '#ffffff';
    let sunDirIntensity = 0.8;
    let sunDirGsm = new THREE.Vector3(10, 5, 5);
    if (lighting && lighting.sunLight) {
        const s = lighting.sunLight;
        if (s.color) sunDirColor = s.color;
        if (typeof s.intensity === 'number') sunDirIntensity = s.intensity;
        if (s.position) sunDirGsm = new THREE.Vector3(s.position.x, s.position.y, s.position.z);
    }
    const sunLight = new THREE.DirectionalLight(new THREE.Color(sunDirColor), sunDirIntensity);
    sunLight.position.copy(mapPhysicalToThreeWithRotation(sunDirGsm));
    sunLight.castShadow = true;
    sunLight.shadow.bias = -0.0005;
    sunLight.shadow.mapSize.set(1024, 1024);
    // Default DirectionalLight shadow camera is ±5; magviz scenes routinely
    // span ±50–100 grid units, so the default frustum clips receivers near
    // the scene edges and they sample out-of-bounds depths — visible as
    // misplaced or missing shadows.
    const shadowCam = sunLight.shadow.camera as THREE.OrthographicCamera;
    shadowCam.left = -200; shadowCam.right = 200;
    shadowCam.bottom = -200; shadowCam.top = 200;
    shadowCam.near = 0.5; shadowCam.far = 1000;
    shadowCam.updateProjectionMatrix();
    scene.add(sunLight);

    // Fill light to soften shadows and bring detail on the nightside
    let fillColor: string = '#4444ff';
    let fillIntensity = 0.3;
    let fillGsm = new THREE.Vector3(-5, -5, -5);
    if (lighting && lighting.fillLight) {
        const f = lighting.fillLight;
        if (f.color) fillColor = f.color;
        if (typeof f.intensity === 'number') fillIntensity = f.intensity;
        if (f.position) fillGsm = new THREE.Vector3(f.position.x, f.position.y, f.position.z);
    }
    const fillLight = new THREE.DirectionalLight(new THREE.Color(fillColor), fillIntensity);
    fillLight.position.copy(mapPhysicalToThreeWithRotation(fillGsm));
    scene.add(fillLight);

    // Soft hemisphere light to reduce black faces and ensure visibility from all angles
    const hemi = new THREE.HemisphereLight(0xaabbff, 0x222233, 0.5);
    scene.add(hemi);

    // Add a back light to illuminate planes from the opposite side
    const backLight = new THREE.DirectionalLight(0xffffff, 0.35);
    backLight.position.copy(mapPhysicalToThreeWithRotation(new THREE.Vector3(-sunDirGsm.x, -sunDirGsm.y, -sunDirGsm.z)));
    scene.add(backLight);
}
